import classNames from 'classnames';
import { useRouter } from 'next/navigation';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import Logo from '@/components/atoms/logo/logo';
import Typography from '@/components/atoms/typography/typography';
import HubspotFormItem from '@/components/modules/hubspot-form/hubspot-form-item/hubspot-form-item';
import Button from '@/components/molecules/button/button';
import FeatureList from '@/components/organisms/feature-list/feature-list';
import PriceDetails from '@/components/organisms/price-details/price-details';
import { SearchBar } from '@/components/organisms/search-bar/search-bar';
import { SearchBarDefaultValues } from '@/components/organisms/search-bar/search-bar.types';
import variables from '@/components/organisms/search-widget/search-widget.variables';
import Lightbox from '@/components/utilities/lightbox/lightbox';
import Link from '@/components/utilities/link/link';
import { trackClickBookingEvent } from '@/integrations/tracking/google-tag-manager/scripts';
import { useHotelContext } from '@/libraries/contexts/hotel-context/hotel-context';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import { useSearchContext } from '@/libraries/contexts/search-context';
import { capitalizeFirstLetter } from '@/libraries/formatters/capitalize-first-letter';
import { updateUrlWithParameters } from '@/libraries/formatters/update-url-with-parameters';
import useCurrencyFormatter from '@/libraries/formatters/use-currency-formatter';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';
import { getLogoName } from '@/libraries/getters/get-logo-name';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import SearchBarPersons from '../search-bar-persons/search-bar-persons';
import SearchWidgetModal from './search-widget-modal/search-widget-modal';
import styles from './search-widget.module.scss';

export interface SearchWidgetProps {
  /** The class name to apply */
  className?: string;
  /** Booking flow anchor id to scroll to */
  configurationFlowAnchorLinkId?: string;
  /** Default values for the search bar */
  defaultValues?: SearchBarDefaultValues;
  /** Discount total */
  discount?: number;
  /** Discount per person */
  discountPerPerson?: number;
  /** Duration information of the offer */
  duration?: number;
  /** Hubspot form to display */
  hubspotForm?: Content.Components['HubspotFormDto'] | null;
  /** Hubspot form button text */
  hubspotFormButtonText: string;
  /** Facilities list to display */
  includedProducts?: Content.Components['ProductDto'][];
  /** Information links to display at the bottom of the widget */
  infoLinks?: Content.Properties.Link[];
  /** Normal price to display*/
  normalPrice?: number;
  /** Normal price per person to display */
  normalPricePerPerson?: number;
  /** Price to display */
  price?: number;
  /** Price breakdown */
  priceDetails?: Content.Components['PriceDetailsDto'];
  /** Price per person to display */
  pricePerPerson?: number;
  /** Search settings for search bar */
  searchSettings: Content.Components['SearchSettingsDto'];
}

const SearchWidget = forwardRef<HTMLDivElement, SearchWidgetProps>(
  (
    {
      configurationFlowAnchorLinkId,
      className,
      defaultValues,
      discount,
      discountPerPerson,
      duration,
      hubspotForm,
      hubspotFormButtonText,
      includedProducts = [],
      infoLinks = [],
      normalPrice,
      normalPricePerPerson,
      price,
      priceDetails,
      pricePerPerson,
      searchSettings,
    },
    ref,
  ) => {
    const isLaptop = useWindowSize('laptop');
    const { t } = useI18n();
    const router = useRouter();
    const currencyFormatter = useCurrencyFormatter();
    const locale = getLocale();

    const {
      brand,
      hotelDetails,
      searchWidgetState,
      setConfigurationFlowAnchorLinkId,
      trackingData,
    } = usePageDetailsContext();
    const { url } = useHotelContext();
    const {
      activeSection,
      isClickedEditSearch,
      setActiveSection,
      setIsClickedEditSearch,
    } = useSearchContext();
    const searchContainerRef = useRef<{ search: () => void }>(null);
    const [showSearchWidgetModal, setShowSearchWidgetModal] = useState(false);
    const [showPriceDetails, setShowPriceDetails] = useState(false);
    const [isHubspotFormShown, setIsHubspotFormShown] = useState(false);

    const isContactForm = searchWidgetState === 'contactForm';
    const isFallback = searchWidgetState === 'fallback';
    const isNoOffersFound = searchWidgetState === 'noOffersFound';
    const isOfferFound = searchWidgetState === 'offerFound';
    const isOfferNotFound = searchWidgetState === 'offerNotFound';
    const isOfferSuggested = searchWidgetState === 'offerSuggested';
    const isSoldOut = searchWidgetState === 'soldOut';
    const isPredefinedSearch = searchWidgetState === 'predefinedSearchPage';
    const isPredefinedSearchPageNoOffersFound =
      searchWidgetState === 'predefinedSearchPageNoOffersFound';

    const showPricePerPerson =
      !!pricePerPerson && (isContactForm || isOfferSuggested);

    const showTotalPrice = !!price && (isOfferFound || isPredefinedSearch);

    const hasOfferWrapper =
      isOfferFound || isOfferSuggested || isPredefinedSearch;

    const hasNormalPriceOffer =
      discount !== 0 && normalPrice && hasOfferWrapper && showTotalPrice;

    const hasNormalPricePerPersonOffer =
      discountPerPerson !== 0 &&
      normalPricePerPerson &&
      hasOfferWrapper &&
      showPricePerPerson;

    const hasPriceWrapper =
      isContactForm || isOfferFound || isOfferSuggested || isPredefinedSearch;

    const hasNormalPrice =
      (isOfferFound || isPredefinedSearch) && !!normalPrice && discount !== 0;

    const hasNormalPricePerPerson =
      isOfferSuggested && !!normalPricePerPerson && discountPerPerson !== 0;

    const hasContactButton = isContactForm || isOfferNotFound;

    const hasFacilitiesDetails =
      isContactForm || isOfferFound || isOfferSuggested || isPredefinedSearch;

    const hasInfoLinks =
      isContactForm || isOfferFound || isOfferSuggested || isPredefinedSearch;

    const hasRedirectButton = isFallback || isSoldOut;

    const hasScrollButton =
      isOfferFound || isOfferSuggested || isPredefinedSearch;

    const hasSearchBarContainer =
      isNoOffersFound || isOfferFound || isOfferSuggested;

    useEffect(() => {
      setConfigurationFlowAnchorLinkId(configurationFlowAnchorLinkId);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (activeSection === -1) {
        setShowSearchWidgetModal(false);
      } else {
        setShowSearchWidgetModal(true);
      }
    }, [activeSection]);

    useEffect(() => {
      if (isClickedEditSearch) {
        setActiveSection(variables.isPrimary ? 0 : 1);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isClickedEditSearch]);

    let priceLabel = t('items.common.words.total');

    switch (searchWidgetState) {
      case 'contactForm':
        {
          priceLabel = `${capitalizeFirstLetter(`${t('items.common.words.from')}`)}`;
        }
        break;

      case 'offerSuggested':
        {
          priceLabel = t('items.common.words.fromPricePerPerson');
        }
        break;
    }

    const handleModalOpen = (value: boolean) => {
      setIsClickedEditSearch(value);

      if (!value) {
        setActiveSection(-1);
      }
    };

    const handleRedirect = () => {
      const hasRoomDistribution =
        searchSettings.selectedPaxDistribution?.hasRoomDistribution || false;

      const adultsNumber =
        searchSettings.selectedPaxDistribution?.adultsNo.toString() || '';
      const childrenAges =
        searchSettings.selectedPaxDistribution?.childrenAges
          ?.map(String)
          .join(',') || '';
      const adultsPerRoom =
        searchSettings.selectedPaxDistribution?.adultsPerRoom
          ?.map(String)
          .join('|') || '';
      const childrenAgesPerRoom =
        searchSettings.selectedPaxDistribution?.childrenAgesPerRoom
          ?.map(room => room.join(','))
          .join('|') || '';

      const parameters = new URLSearchParams({
        batchSize: searchSettings.batchSize.toString() || '',
        departureDate: searchSettings.selectedDepartureDate
          ? new Date(
              searchSettings.selectedDepartureDate.toString(),
            ).toDateString()
          : '',
        departureIds: searchSettings.selectedDepartureId?.toString() || '',
        destinationIds:
          (searchSettings.selectedDestinationIds &&
            (searchSettings.selectedDestinationIds
              .map(value => value.toString())
              .join(',') as string)) ||
          '',
        durationType: searchSettings.selectedPeriodType || '',
        locale: getCulture(locale),
        sortBy: searchSettings.selectedSortBy ?? 'websitePriority',
      });

      if (hasRoomDistribution) {
        parameters.append('adultsPerRoom', adultsPerRoom);
        parameters.append('childrenAgesPerRoom', childrenAgesPerRoom);
      } else {
        parameters.append('childrenAges', childrenAges);
        parameters.append('adultsNumber', adultsNumber);
      }

      const parametersObject: Record<string, any> = {};

      parameters.forEach((value, key) => {
        parametersObject[key] = value;
      });

      updateUrlWithParameters({
        newParameters: parametersObject,
        overwrite: true,
        redirectUrl: searchSettings.searchPageUrl,
        router,
        shouldNavigate: true,
      });
    };

    const getPrimaryPriceSection = () => {
      return (
        <div className={styles.mainPrice}>
          <Typography
            className={styles.priceLabel}
            tag="label"
            tagStyle="headingMedium"
          >
            {priceLabel}
          </Typography>

          <Typography
            className={styles.price}
            tag="label"
            tagStyle="headingMedium"
          >
            {showPricePerPerson
              ? pricePerPerson && currencyFormatter(pricePerPerson)
              : price && currencyFormatter(price)}
          </Typography>
        </div>
      );
    };

    const getSecondaryPriceSection = () => {
      if (isContactForm) {
        return (
          <Typography
            className={styles.priceAndDuration}
            tag="span"
            tagStyle="bodyMedium"
          >
            {`${t('items.common.words.perPerson')} / ${duration} ${t('items.common.words.days')}`}
          </Typography>
        );
      }

      if (
        showTotalPrice &&
        typeof pricePerPerson === 'number' &&
        pricePerPerson > 0
      ) {
        return (
          <Typography
            className={styles.pricePerPerson}
            tag="span"
            tagStyle="bodyMedium"
          >
            {`${t('items.common.words.pricePerPerson')}: ${currencyFormatter(pricePerPerson)}`}
          </Typography>
        );
      }

      if (!showTotalPrice && typeof price === 'number' && price > 0) {
        return (
          <Typography
            className={styles.priceTotal}
            tag="span"
            tagStyle="bodyMedium"
          >
            {`${t('items.common.words.totalPrice')}: ${currencyFormatter(price)}`}
          </Typography>
        );
      }
    };

    return (
      <React.Fragment>
        <div ref={ref} className={classNames(styles.component, className)}>
          <div className={styles.logoWrapper}>
            <Logo
              className={styles.logo}
              name={getLogoName({ brand, type: 'Symbol' })}
            />
          </div>

          <div className={styles.searchWidgetWrapper}>
            {hasNormalPriceOffer && (
              <div className={styles.offerWrapper}>
                <Typography
                  className={styles.offerText}
                  tag="span"
                  tagStyle="bodyMediumBold"
                >
                  {`${t('items.search.productCard.save')} ${currencyFormatter(discount as number)}`}
                </Typography>
              </div>
            )}

            {hasNormalPricePerPersonOffer && (
              <div className={styles.offerWrapper}>
                <Typography
                  className={styles.offerText}
                  tag="div"
                  tagStyle="bodyMediumBold"
                >
                  {`${t('items.search.productCard.save')} ${currencyFormatter(discountPerPerson as number)} `}
                  <span className={styles.perPersonOfferText}>
                    {t('items.common.words.perPerson')}
                  </span>
                </Typography>
              </div>
            )}

            <div className={styles.contentWrapper}>
              {!!((price || pricePerPerson) && hasPriceWrapper) && (
                <div className={styles.priceWrapper}>
                  {hasNormalPrice && (
                    <div className={styles.normalPrice}>
                      <Typography tag="span" tagStyle="bodyMedium">
                        {currencyFormatter(normalPrice)}
                      </Typography>
                    </div>
                  )}

                  {hasNormalPricePerPerson && (
                    <div className={styles.normalPricePerPerson}>
                      <Typography tag="span" tagStyle="bodyMedium">
                        {currencyFormatter(normalPricePerPerson)}
                      </Typography>
                    </div>
                  )}

                  {getPrimaryPriceSection()}

                  {getSecondaryPriceSection()}

                  {(infoLinks.length > 0 || priceDetails) && hasInfoLinks && (
                    <div className={styles.linksWrapper}>
                      {infoLinks.length > 0 &&
                        infoLinks.map((item, index) => (
                          <div key={index} className={styles.link}>
                            <Link {...item}>{item.text}</Link>
                          </div>
                        ))}

                      {priceDetails && (
                        <button
                          className={styles.link}
                          onClick={() => setShowPriceDetails(true)}
                        >
                          <Typography
                            className={styles.linksWrapper}
                            tag="span"
                            tagStyle="bodyMedium"
                          >
                            {t('items.common.words.priceDetails')}
                          </Typography>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {isFallback && (
                <div className={styles.noResultsWrapper}>
                  <Typography
                    className={styles.noResultsHeader}
                    tag="label"
                    tagStyle="headingMedium"
                  >
                    {t('items.searchWidget.states.fallbackHeadline')}
                  </Typography>

                  <Typography
                    className={styles.noResultsDescription}
                    tag="span"
                    tagStyle="bodyMedium"
                  >
                    {t('items.searchWidget.states.fallbackDescription', {
                      destinationName: hotelDetails?.destinationName || '',
                    })}
                  </Typography>
                </div>
              )}

              {isNoOffersFound && (
                <div className={styles.noResultsWrapper}>
                  <Typography
                    className={styles.noResultsHeader}
                    tag="label"
                    tagStyle="headingMedium"
                  >
                    {t('items.searchWidget.states.noOffersFoundHeadline')}
                  </Typography>

                  <Typography
                    className={styles.noResultsDescription}
                    tag="span"
                    tagStyle="bodyMedium"
                  >
                    {t('items.searchWidget.states.noOffersFoundDescription', {
                      destinationName: hotelDetails?.destinationName || '',
                    })}
                  </Typography>
                </div>
              )}

              {isOfferNotFound && (
                <div className={styles.noResultsWrapper}>
                  <Typography
                    className={styles.noResultsHeader}
                    tag="label"
                    tagStyle="headingMedium"
                  >
                    {t('items.searchWidget.states.offerNotFoundHeadline')}
                  </Typography>

                  <Typography
                    className={styles.noResultsDescription}
                    tag="span"
                    tagStyle="bodyMedium"
                  >
                    {t('items.searchWidget.states.offerNotFoundDescription', {
                      destinationName: hotelDetails?.destinationName || '',
                    })}
                  </Typography>
                </div>
              )}

              {isSoldOut && (
                <div className={styles.noResultsWrapper}>
                  <Typography
                    className={styles.noResultsHeader}
                    tag="label"
                    tagStyle="headingMedium"
                  >
                    {t('items.searchWidget.states.soldOutHeadline')}
                  </Typography>

                  <Typography
                    className={styles.noResultsDescription}
                    tag="span"
                    tagStyle="bodyMedium"
                  >
                    {t('items.searchWidget.states.soldOutDescription', {
                      destinationName: hotelDetails?.destinationName || '',
                    })}
                  </Typography>
                </div>
              )}

              {hasSearchBarContainer && (
                <SearchBar
                  ref={searchContainerRef}
                  buttonMode
                  className={styles.searchBar}
                  isWidget
                  propDefaultValues={defaultValues}
                  searchSettings={searchSettings}
                />
              )}

              {isPredefinedSearch && (
                <SearchBarPersons searchSettings={searchSettings} />
              )}

              {isPredefinedSearchPageNoOffersFound && (
                <div className={styles.noResultsWrapper}>
                  <Typography
                    className={styles.noResultsHeader}
                    tag="label"
                    tagStyle="headingMedium"
                  >
                    {t(
                      'items.searchWidget.states.predefinedSearchPageNoOffersFoundHeadline',
                    )}
                  </Typography>

                  <Typography
                    className={styles.noResultsDescription}
                    tag="span"
                    tagStyle="bodyMedium"
                  >
                    {t(
                      'items.searchWidget.states.predefinedSearchPageNoOffersFoundDescription',
                    )}
                  </Typography>
                </div>
              )}

              {includedProducts.length > 0 && hasFacilitiesDetails && (
                <div className={styles.facilitiesDetails}>
                  <Typography
                    className={styles.facilitiesHeading}
                    tag="span"
                    tagStyle="bodyMediumBold"
                  >
                    {t('items.search.productCard.includedInTrip')}
                  </Typography>

                  <FeatureList features={includedProducts} />
                </div>
              )}

              {hasContactButton && hubspotForm && (
                <div className={styles.bookingButtonWrapper}>
                  <Button
                    onClick={() => {
                      setIsHubspotFormShown(true);

                      if (!trackingData?.gtmDataLayer?.items?.[0]) return;

                      trackClickBookingEvent({
                        items: [
                          {
                            ...trackingData.gtmDataLayer.items[0],
                            price,
                            quantity: 1,
                          },
                        ],
                        travel_details:
                          trackingData.gtmDataLayer.travel_details,
                      });
                    }}
                    text={hubspotFormButtonText}
                    variant="primary"
                  />
                </div>
              )}

              {hasRedirectButton && (
                <div className={styles.bookingButtonWrapper}>
                  <Button
                    onClick={handleRedirect}
                    text={t('items.searchWidget.findAlternative')}
                    variant="primary"
                  />
                </div>
              )}

              {hasScrollButton && configurationFlowAnchorLinkId && (
                <div className={styles.bookingButtonWrapper}>
                  <Button
                    text={
                      isPredefinedSearch
                        ? t('items.searchWidget.selectDeparture')
                        : t('items.searchWidget.editRoomOption')
                    }
                    url={`#${configurationFlowAnchorLinkId}`}
                    variant="primary"
                  />
                </div>
              )}
            </div>

            {hasSearchBarContainer &&
              (isLaptop ? (
                <SearchWidgetModal
                  defaultValues={defaultValues}
                  isOpen={showSearchWidgetModal}
                  searchSettings={searchSettings}
                  setIsOpen={value => handleModalOpen(value)}
                />
              ) : (
                showSearchWidgetModal && (
                  <SearchBar
                    isWidget
                    propDefaultValues={defaultValues}
                    searchSettings={searchSettings}
                    shouldRefresh
                  />
                )
              ))}

            {hubspotForm && (
              <Lightbox
                isOpen={isHubspotFormShown}
                onClose={() => setIsHubspotFormShown(false)}
              >
                <HubspotFormItem form={hubspotForm} />
              </Lightbox>
            )}
          </div>

          {priceDetails && (
            <PriceDetails
              isOpen={showPriceDetails}
              priceDetails={priceDetails}
              setIsOpen={setShowPriceDetails}
              url={url}
            />
          )}
        </div>
      </React.Fragment>
    );
  },
);

SearchWidget.displayName = 'SearchWidget';

export default SearchWidget;
